import React, { useState, useEffect, useRef } from "react";
import { Modal, Table, Pagination, Button, message, Row, Col, Radio, Collapse, Checkbox, Tree, Form, Typography, Space, Input } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import axiosInstance from "../../../../api/Axios";
import useCommonListing from "../../../../components/AdminComponents/customHooks/useCommonListing";
import CommonTopDetails from "../../../../components/AdminComponents/CommonTopDetails";
import { capitalizeWords } from "../../../../components/icons/ApplicationColor";

const AddLibraryModal = ({ open, onCancel, customerId, selectedCustomerIds, parentKeyId, onSuccess }) => {
    const isBulk = Array.isArray(selectedCustomerIds) && selectedCustomerIds.length > 0;
    const [loadCourseAgain, setLoadCourseAgain] = useState(false);
    const {
        data,
        itemsLength,
        loading,
        filterData,
        onShowSizeChange,
        onPageChange,
        handleSearchChange,
        setFilterUrl,
        setLoading: setCourseLoading,
    } = useCommonListing({
        passSize: 10,
        passPage: 1,
        passURL: isBulk
            ? `/customer/parentcourselibrary/?parent_key_id=${parentKeyId}&status=True`
            : `/customer/parentcourselibrary/?customer_id=${customerId}&parent_key_id=${parentKeyId}&status=True`,
        includeStatus: false,
        loadAgain: loadCourseAgain,
    });

    const customerListing = useCommonListing({
        passSize: 10,
        passPage: 1,
        passURL: `/customer/customerslist/?parent_key_id=${parentKeyId}`,
        includeStatus: true,
    });

    const {
        data: customerData,
        itemsLength: customerItemsLength,
        loading: customerLoading,
        filterData: customerFilterData,
        onShowSizeChange: onCustomerShowSizeChange,
        onPageChange: onCustomerPageChange,
        handleSearchChange: handleCustomerSearchChange,
        classification,
        setLoading: setCustomerLoading,
    } = customerListing;

    const getColumnTitle = (defaultTitle) => {
        if (classification) {
            return defaultTitle
                .replace("Customer", capitalizeWords(classification))
                .replace("Organization", capitalizeWords(classification));
        }
        return defaultTitle;
    };

    const {
        pageSize: customerPageSize,
        currentPage: customerCurrentPage,
    } = customerFilterData;

    const [modalSelectedCustomerIds, setModalSelectedCustomerIds] = useState(selectedCustomerIds || []);

    const customerHeaderRef = useRef(false);

    const onCustomerSelectionChange = (newSelectedRowKeys) => {
        if (customerHeaderRef.current) {
            customerHeaderRef.current = false;
            return;
        }
        setModalSelectedCustomerIds(newSelectedRowKeys);
        if (selectedRowKeys.length > 0) {
            handleCheckAssignments(selectedRowKeys, newSelectedRowKeys);
        }
        setLoadCourseAgain(prev => !prev);
    };

    const customerRowSelection = {
        selectedRowKeys: modalSelectedCustomerIds,
        onChange: onCustomerSelectionChange,
        columnWidth: 50,
        preserveSelectedRowKeys: true,
        onSelectAll: (selected) => {
            customerHeaderRef.current = true;
            if (selected) {
                handleSelectAllCustomers();
            } else {
                setModalSelectedCustomerIds([]);
                if (selectedRowKeys.length > 0) {
                    handleCheckAssignments(selectedRowKeys, []);
                }
                setLoadCourseAgain(prev => !prev);
            }
        },
    };

    const handleSelectAllCustomers = async () => {
        setCustomerLoading(true);
        try {
            const res = await axiosInstance.get(`/customer/customerslist/?parent_key_id=${parentKeyId}&page_size=${customerItemsLength || 1000}&status=True`);
            const allIds = (res.data?.results || []).map(item => item.id);
            setModalSelectedCustomerIds(allIds);
            if (selectedRowKeys.length > 0) {
                handleCheckAssignments(selectedRowKeys, allIds);
            }
            setLoadCourseAgain(prev => !prev);
            message.success(`Selected all ${allIds.length} customers.`);
        } catch (err) {
            console.error("Error selecting all customers:", err);
            message.error("Failed to select all customers.");
        } finally {
            setCustomerLoading(false);
        }
    };



    const { pageSize, currentPage } = filterData;
    const [filterOptions, setFilterOptions] = useState({
        courseTypes: [],
        modes: [],
        languages: [],
        categories: [],
        tags: [],
    });
    const [selectedCourseType, setSelectedCourseType] = useState("");
    const [defaultAllType, setDefaultAllType] = useState("");
    const [selectedModes, setSelectedModes] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [leafCategoryIds, setLeafCategoryIds] = useState([]);

    const extractLeafIds = (categories) => {
        let leaves = [];
        categories.forEach(cat => {
            if (cat.second_categories && cat.second_categories.length > 0) {
                cat.second_categories.forEach(sub => {
                    if (sub.third_categories && sub.third_categories.length > 0) {
                        sub.third_categories.forEach(third => leaves.push(third.id));
                    } else {
                        leaves.push(sub.id);
                    }
                });
            } else {
                leaves.push(cat.id);
            }
        });
        return leaves;
    };

    useEffect(() => {
        if (open) {
            const fetchFilters = async () => {
                try {
                    const res = await axiosInstance.get(`/customer/filter/?organization=${parentKeyId}`);
                    const data = res?.data || {};
                    setFilterOptions({
                        courseTypes: data.course_types || [],
                        modes: data.modes || [],
                        languages: data.languages || [],
                        categories: data.categories || [],
                        tags: data.tags || [],
                    });
                    const leaves = extractLeafIds(data.categories || []);
                    setLeafCategoryIds(leaves);

                    const allType = data?.course_types?.find(ct => ct.course_type.toLowerCase() === 'all');
                    if (allType) {
                        setSelectedCourseType(allType.course_type);
                        setDefaultAllType(allType.course_type);
                    }
                } catch (err) {
                    console.error('Error fetching filter options', err);
                }
            };
            fetchFilters();
        }
    }, [open, parentKeyId]);

    useEffect(() => {
        const params = new URLSearchParams();
        if (selectedCourseType) params.append('course_type', selectedCourseType);
        if (selectedModes.length) params.append('mode', selectedModes.join(','));
        if (selectedLanguages.length) params.append('language', selectedLanguages.join(','));

        const validCategories = selectedCategories.filter(id => leafCategoryIds.includes(id));
        if (validCategories.length) params.append('category', validCategories.join(','));

        if (selectedTags.length) params.append('tags', selectedTags.join(','));
        const filterUrl = params.toString();

        if (filterData.filter_url !== filterUrl) {
            setFilterUrl(filterUrl);
        }
    }, [selectedCourseType, selectedModes, selectedLanguages, selectedCategories, selectedTags, setFilterUrl, filterData.filter_url, leafCategoryIds]);

    useEffect(() => {
        setSelectedRowKeys([]);
        setLookupData([]);
        setSelectedLookupKeys([]);
    }, [
        selectedCourseType,
        selectedModes,
        selectedLanguages,
        selectedCategories,
        selectedTags,
        filterData.search
    ]);

    const columns = [
        {
            title: "Course ID",
            dataIndex: "course_id",
            key: "course_id",
            width: 100,
        },
        {
            title: "Course Name",
            dataIndex: "name",
            key: "name",
            width: 200,
            ellipsis: true,
        },
        {
            title: "Mode",
            dataIndex: "mode",
            key: "mode",
            width: 120,
        },
        {
            title: "Course Type",
            dataIndex: "course_type",
            key: "course_type",
            width: 120,
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            width: 120,
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            width: 120,
        },

    ];

    const customerColumns = [
        {
            title: getColumnTitle("Customer ID"),
            dataIndex: "client_id",
            key: "client_id",
            width: 90,
        },
        {
            title: getColumnTitle("Customer Name"),
            dataIndex: "name",
            key: "name",
            width: 200,
        },
    ];


    const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
    const [lookupData, setLookupData] = React.useState([]);
    const [checkingStatus, setCheckingStatus] = React.useState(false);

    const courseHeaderRef = useRef(false);

    const onSelectChange = (newSelectedRowKeys) => {
        if (courseHeaderRef.current) {
            courseHeaderRef.current = false;
            return;
        }
        setSelectedRowKeys(newSelectedRowKeys);
        handleCheckAssignments(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: 50,
        preserveSelectedRowKeys: true,
        onSelectAll: (selected) => {
            courseHeaderRef.current = true;
            if (selected) {
                handleSelectAllCourses();
            } else {
                setSelectedRowKeys([]);
                handleCheckAssignments([]);
            }
        },
    };

    const handleSelectAllCourses = async () => {
        setCourseLoading(true);
        try {
            let passUrl = isBulk
                ? `/customer/parentcourselibrary/?parent_key_id=${parentKeyId}&status=True`
                : `/customer/parentcourselibrary/?customer_id=${customerId}&parent_key_id=${parentKeyId}&status=True`;

            const joiner = passUrl.includes("?") ? "&" : "?";
            passUrl += `${joiner}page_size=${itemsLength || 1000}`;
            if (filterData.search) passUrl += `&search=${filterData.search}`;
            if (filterData.filter_url) passUrl += `&${filterData.filter_url}`;

            const res = await axiosInstance.get(passUrl);
            const allIds = (res.data?.results || []).map(item => item.id);
            setSelectedRowKeys(allIds);
            handleCheckAssignments(allIds);
            message.success(`Selected all ${allIds.length} courses.`);
        } catch (err) {
            console.error("Error selecting all courses:", err);
            message.error("Failed to select all courses.");
        } finally {
            setCourseLoading(false);
        }
    };


    const getCookie = (name) => {
        const cookies = document.cookie.split("; ");
        for (let i = 0; i < cookies.length; i++) {
            const [cookieName, cookieValue] = cookies[i].split("=");
            if (cookieName === name) {
                return cookieValue;
            }
        }
        return null;
    };

    const handleCheckAssignments = async (keys = selectedRowKeys, orgIds = modalSelectedCustomerIds) => {
        if (keys.length === 0) {
            setLookupData([]);
            return;
        }

        const csrfToken = getCookie("csrftoken");
        setCheckingStatus(true);
        try {
            const payload = {
                course_ids: keys,
                organization_ids: isBulk ? orgIds : [Number(customerId)],
            };

            const response = await axiosInstance.post(
                "/courseorganizationlookup/",
                payload,
                {
                    headers: {
                        "X-CSRFToken": csrfToken,
                    },
                }
            );
            const data = response.data?.data || [];
            setLookupData(data);
            if (isBulk) {
                // In bulk, use combined key for initial selection
                setSelectedLookupKeys(data.map(item => `${item.course_id}-${item.organization_id}`));
            } else {
                setSelectedLookupKeys(data.map(item => item.course_id));
            }
        } catch (error) {
            console.error("Error checking assignments:", error);
        } finally {
            setCheckingStatus(false);
        }
    };

    const lookupColumns = [
        {
            title: "Course Id",
            dataIndex: "course_id",
            key: "course_id",
        },
        {
            title: "Course Name",
            dataIndex: "course_name",
            key: "course_name",
        },
        {
            title: getColumnTitle("Organization"),
            dataIndex: "organization_name",
            key: "organization_name",
        },
        {
            title: "Assigned Status",
            dataIndex: "assigned_status",
            key: "assigned_status",
            render: (status) => (
                !status ? <CheckCircleOutlined style={{ color: "green", fontSize: '18px' }} /> : <CloseCircleOutlined style={{ color: "red", fontSize: '18px' }} />
            ),
            align: 'center',
        },
        {
            title: "Message",
            dataIndex: "Message",
            key: "Message",
        },
    ];

    // Selection for the lookup table (Combined Selection)
    const [selectedLookupKeys, setSelectedLookupKeys] = React.useState([]);
    const onLookupSelectChange = (newSelectedKeys) => {
        setSelectedLookupKeys(newSelectedKeys);
    };
    const lookupRowSelection = {
        selectedRowKeys: selectedLookupKeys,
        onChange: onLookupSelectChange,
        columnWidth: 50,
        getCheckboxProps: (record) => ({
            disabled: record.assigned_status === true,
        }),
    };


    const [savingStatus, setSavingStatus] = React.useState(false);

    const handleSaveAssignment = async () => {
        let finalCourseIds = [];
        if (isBulk) {
            // Extract unique course IDs from combined keys (e.g., "123-45")
            finalCourseIds = [...new Set(selectedLookupKeys.map(key => key.split('-')[0]))];
        } else {
            finalCourseIds = selectedLookupKeys;
        }

        if (finalCourseIds.length === 0) {
            message.warning("Please select courses from the Combined Selection table to save.");
            return;
        }

        setSavingStatus(true);
        const csrfToken = getCookie("csrftoken");
        try {
            const payload = {
                course_ids: finalCourseIds,
                organization_ids: isBulk ? modalSelectedCustomerIds : [Number(customerId)],
                type: "organization_assign",
            };

            await axiosInstance.post("/courseorganizationbulkassign/", payload, {
                headers: {
                    "X-CSRFToken": csrfToken,
                },
            });
            message.success(isBulk ? "Courses assigned to selected customers successfully!" : "Courses assigned successfully!");
            if (onSuccess) onSuccess();
            onCancel();
        } catch (error) {
            console.error("Error saving assignment:", error);
            message.error("Failed to save assignment.");
        } finally {
            setSavingStatus(false);
        }
    };

    const handleClearAll = () => {
        setSelectedCourseType(defaultAllType);
        setSelectedModes([]);
        setSelectedLanguages([]);
        setSelectedCategories([]);
        setSelectedTags([]);
        message.info("Filters cleared");
    };

    const [categorySearch, setCategorySearch] = useState('');

    const filterCategories = (categories, search) => {
        if (!search) return categories;
        return categories.filter(cat => {
            const matchesSearch = cat.name.toLowerCase().includes(search.toLowerCase());
            const filteredChildren = cat.second_categories ? filterCategories(cat.second_categories, search) : [];
            return matchesSearch || filteredChildren.length > 0;
        });
    };

    const filteredCategories = filterCategories(filterOptions.categories, categorySearch);

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            width={1600}
            footer={null}
            destroyOnClose
            style={{ top: 20 }}
            bodyStyle={{ height: 'calc(100vh - 100px)', overflowY: 'hidden', padding: 0 }}
            closable={true}
        >
            <style>
                {`
                    .filter-sidebar::-webkit-scrollbar {
                        width: 5px;
                    }
                    .filter-sidebar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .filter-sidebar::-webkit-scrollbar-thumb {
                        background: #d9d9d9;
                        border-radius: 10px;
                    }
                    .filter-sidebar::-webkit-scrollbar-thumb:hover {
                        background: #bfbfbf;
                    }
                    .filter-title {
                        font-weight: 700 !important;
                        font-size: 16px !important;
                        margin-bottom: 1rem !important;
                        color: #262626;
                    }
                    .filter-option-item {
                        padding: 6px 12px;
                        border-radius: 4px;
                        transition: all 0.2s;
                        width: 100%;
                        cursor: pointer;
                    }
                    .filter-option-item:hover {
                        background-color: #f0f5ff;
                    }
                    .ant-radio-wrapper {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        margin-right: 0 !important;
                        font-size: 14px;
                    }
                    .ant-collapse-ghost > .ant-collapse-item > .ant-collapse-header {
                        padding: 12px 0 !important;
                        font-weight: 600;
                        color: #262626;
                        border-bottom: 1px solid #f0f0f0;
                    }
                    .ant-collapse-ghost > .ant-collapse-item {
                        border-bottom: none !important;
                    }
                    .ant-collapse-expand-icon {
                        order: 1;
                        margin-inline-start: auto !important;
                    }
                    .ant-collapse-header-text {
                        order: 0;
                    }
                    .ant-modal-close {
                        z-index: 1010;
                    }
                    .search-input-container {
                        padding: 8px 0;
                        margin-bottom: 8px;
                    }
                    .main-content-column::-webkit-scrollbar {
                        width: 6px;
                    }
                    .main-content-column::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .main-content-column::-webkit-scrollbar-thumb {
                        background: #d9d9d9;
                        border-radius: 10px;
                    }
                `}
            </style>
            <Row gutter={0} style={{ height: '100%', paddingTop: '45px' }}>
                {/* Filter Sidebar */}
                <Col span={6} className="filter-sidebar" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', padding: '1.5rem', borderRight: '1px solid #f0f0f0' }}>
                    <Typography.Title level={5} className="filter-title">
                        Filter
                    </Typography.Title>

                    {/* Course Types - Top Level */}
                    <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #f0f0f0' }}>
                        <Radio.Group
                            value={selectedCourseType}
                            onChange={e => setSelectedCourseType(e.target.value)}
                            style={{ width: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                {filterOptions.courseTypes.map(ct => (
                                    <div key={ct.course_type} className="filter-option-item" onClick={() => setSelectedCourseType(ct.course_type)}>
                                        <Radio value={ct.course_type}>
                                            {ct.course_type}
                                        </Radio>
                                    </div>
                                ))}
                            </Space>
                        </Radio.Group>
                    </div>

                    <Collapse ghost defaultActiveKey={['modes', 'languages', 'categories', 'tags']} expandIconPosition="end">
                        <Collapse.Panel header="Modes" key="modes">
                            <Checkbox.Group
                                value={selectedModes}
                                onChange={setSelectedModes}
                                style={{ width: '100%' }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                    {filterOptions.modes.map(m => (
                                        <div key={m.mode} className="filter-option-item">
                                            <Checkbox value={m.mode}>
                                                {m.mode}
                                            </Checkbox>
                                        </div>
                                    ))}
                                </Space>
                            </Checkbox.Group>
                        </Collapse.Panel>

                        <Collapse.Panel header="Language" key="languages">
                            <Checkbox.Group
                                value={selectedLanguages}
                                onChange={setSelectedLanguages}
                                style={{ width: '100%' }}
                            >
                                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                    {filterOptions.languages.map(l => (
                                        <div key={l.Language} className="filter-option-item">
                                            <Checkbox value={l.Language}>
                                                {l.Language}
                                            </Checkbox>
                                        </div>
                                    ))}
                                </Space>
                            </Checkbox.Group>
                        </Collapse.Panel>

                        <Collapse.Panel header="Tags" key="tags">
                            <div style={{ maxHeight: '200px', overflowY: 'auto', overflowX: 'hidden' }}>
                                <Checkbox.Group
                                    value={selectedTags}
                                    onChange={setSelectedTags}
                                    style={{ width: '100%' }}
                                >
                                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                        {filterOptions.tags.map(t => (
                                            <div key={t.name} className="filter-option-item">
                                                <Checkbox value={t.name}>
                                                    {t.name}
                                                </Checkbox>
                                            </div>
                                        ))}
                                    </Space>
                                </Checkbox.Group>
                            </div>
                        </Collapse.Panel>

                        <Collapse.Panel header="Categories" key="categories">
                            <div className="search-input-container">
                                <Input
                                    placeholder="Search categories..."
                                    prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
                                    value={categorySearch}
                                    onChange={e => setCategorySearch(e.target.value)}
                                    size="small"
                                />
                            </div>
                            <div style={{ paddingTop: '8px' }}>
                                <Tree
                                    treeData={filteredCategories.map(cat => ({
                                        title: cat.name,
                                        key: cat.id,
                                        children: (cat.second_categories || []).map(sub => ({
                                            title: sub.name,
                                            key: sub.id,
                                            children: (sub.third_categories || []).map(third => ({
                                                title: third.name,
                                                key: third.id,
                                            })),
                                        })),
                                    }))}
                                    checkedKeys={selectedCategories}
                                    onCheck={(checkedKeys) => setSelectedCategories(checkedKeys)}
                                    checkable
                                    style={{ background: 'transparent' }}
                                />
                            </div>
                        </Collapse.Panel>
                    </Collapse>

                    <div style={{ marginTop: '2rem', textAlign: 'center', paddingBottom: '1rem' }}>
                        <Button type="link" onClick={handleClearAll} style={{ color: '#8c8c8c' }}>
                            Clear All Filters
                        </Button>
                    </div>
                </Col>
                {/* Main Content */}
                <Col span={18} className="main-content-column" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', padding: '1.5rem', paddingRight: '3rem' }}>
                    <div style={{ paddingLeft: '0.5rem' }}>
                        <Typography.Title level={4} style={{ marginBottom: '1.5rem', marginTop: 0 }}>
                            {isBulk ? "Bulk Action" : "Add from Parent Library"}
                        </Typography.Title>

                        {isBulk && (
                            <div style={{ marginBottom: '2rem' }}>
                                <CommonTopDetails
                                    title={`Selected ${classification ? capitalizeWords(classification) : "Customers"}`}
                                    titleSuffix={`${customerItemsLength || 0} ${classification ? classification.toLowerCase() : "customer"}(s)`}
                                    searchPlaceholder={`Search ${classification ? classification.toLowerCase() : "customers"}...`}
                                    searchAction={handleCustomerSearchChange}
                                    showAddButton={false}
                                    filterAction={false}
                                    marginPass="0px"
                                    paddingInlinepass="0px"
                                />
                                <Table
                                    rowSelection={customerRowSelection}
                                    columns={customerColumns}
                                    dataSource={customerData}
                                    rowKey="id"
                                    size="small"
                                    loading={customerLoading}
                                    className="table-container"
                                    style={{ margin: "1rem" }}
                                    pagination={false}
                                    bordered
                                    scroll={{ x: 600 }}
                                />
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        padding: "1rem 0",
                                        alignItems: "center"
                                    }}
                                >
                                    <Pagination
                                        size="small"
                                        showSizeChanger
                                        onShowSizeChange={onCustomerShowSizeChange}
                                        onChange={onCustomerPageChange}
                                        pageSizeOptions={["5", "10", "15", "20", "50"]}
                                        current={customerCurrentPage}
                                        defaultPageSize={customerPageSize}
                                        total={customerItemsLength}
                                    />
                                </div>
                            </div>
                        )}


                        <CommonTopDetails
                            title="Available Courses"
                            titleSuffix={`${itemsLength || 0} item(s)`}
                            searchPlaceholder="Search..."
                            searchAction={handleSearchChange}
                            showAddButton={false}
                            filterAction={false}
                            marginPass="0px"
                            paddingInlinepass="0px"
                        />

                        <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={data}
                            rowKey="id"
                            size="small"
                            loading={loading}
                            className="table-container"
                            style={{ margin: "1rem" }}
                            pagination={false}
                            scroll={{ x: 800 }}
                            bordered
                        />
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                padding: "1rem 0",
                                alignItems: "center"
                            }}
                        >
                            <Pagination
                                size="small"
                                showSizeChanger
                                onShowSizeChange={onShowSizeChange}
                                onChange={onPageChange}
                                pageSizeOptions={["5", "10", "15", "20", "50"]}
                                current={currentPage}
                                defaultPageSize={pageSize}
                                total={itemsLength}
                            />
                        </div>
                        {lookupData.length > 0 && (
                            <>
                                <div style={{ margin: "1rem 0", fontWeight: "bold" }}>Combined Selection</div>
                                <Table
                                    rowSelection={lookupRowSelection}
                                    columns={lookupColumns}
                                    dataSource={lookupData}
                                    rowKey={(record) => isBulk ? `${record.course_id}-${record.organization_id}` : record.course_id}
                                    size="small"
                                    loading={checkingStatus}
                                    className="table-container"
                                    style={{ margin: "1rem" }}
                                    pagination={true}
                                    bordered
                                />
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "1rem",
                                        margin: "1rem",
                                    }}
                                >
                                    <Button onClick={onCancel}>Cancel</Button>
                                    <Button
                                        type="primary"
                                        onClick={handleSaveAssignment}
                                        loading={savingStatus}
                                    >
                                        Save Assignment
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default AddLibraryModal;

